"use client";

import { useState, useEffect } from "react";
import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const translations = {
  en: {
    email: "Email Address",
    password: "Password",
    submit: "Sign In",
    processing: "Processing"
  },
  zh: {
    email: "电子邮件地址",
    password: "密码",
    submit: "登录",
    processing: "处理中"
  },
  de: {
    email: "E-Mail-Adresse",
    password: "Passwort",
    submit: "Anmelden",
    processing: "Wird bearbeitet"
  },
  ja: {
    email: "メールアドレス",
    password: "パスワード",
    submit: "サインイン",
    processing: "処理中"
  },
  es: {
    email: "Correo Electrónico",
    password: "Contraseña",
    submit: "Iniciar Sesión",
    processing: "Procesando"
  }
};
function InnerCircleForm({
  lang
}: {
  lang: string;
}) {
  const t = translations[lang as keyof typeof translations] || translations.en;
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-blue-600/30 bg-blue-600/5 rounded-lg">
        <p className="font-default italic text-slate-900 text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-slate-200 bg-transparent text-slate-900 hover:bg-slate-100 rounded-lg">
          Return to Login
        </Button>
      </div>;
  }
  const interestOptions = ["Surgical Devices", "Diagnostic Solutions", "Medical Aesthetics", "AI & Digital Health"];
  return <Form onSubmit={handleSubmit} className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default uppercase tracking-widest text-[10px] text-slate-500">
                {t.email}
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@hospital.com" {...field} className="h-12 px-0 bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default text-lg placeholder:text-slate-400" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="password" rules={{
        required: "Password is required"
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default uppercase tracking-widest text-[10px] text-slate-500">
                {t.password}
              </FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••••" {...field} className="h-12 px-0 bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default text-lg placeholder:text-slate-400" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem className="gap-4">
            <FormLabel className="font-default uppercase tracking-widest text-[10px] text-slate-500">
              Areas of Interest
            </FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {interestOptions.map((option, i) => {
            const values: string[] = Array.isArray(field.value) ? field.value : [];
            const checked = values.includes(option);
            return <label key={option} data-index={i} className="flex items-center gap-3 cursor-pointer group">
                      <Checkbox checked={checked} onCheckedChange={c => {
                if (c) {
                  field.onChange([...values, option]);
                } else {
                  field.onChange(values.filter(v => v !== option));
                }
              }} className="rounded-lg border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white" />
                      <span className="font-default text-base text-slate-700 group-hover:text-slate-900 transition-colors">
                        {option}
                      </span>
                    </label>;
          })}
              </div>
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to continue"
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormControl>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={!!field.value} onCheckedChange={c => field.onChange(!!c)} className="mt-1 rounded-lg border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white" />
                <span className="font-default italic text-sm text-slate-500 leading-relaxed">
                  I agree to receive professional updates from MS Biotechnology. We prioritize your data privacy.
                </span>
              </label>
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-600/40 bg-red-50 rounded-lg">
          <p className="font-default text-sm text-red-600">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="group h-14 px-8 bg-blue-600 text-white hover:bg-blue-700 font-default uppercase tracking-widest text-xs font-bold shadow-lg rounded-lg w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {t.processing}
            </> : <>
              {t.submit}
              <ArrowRight className="ml-1 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function Partnership() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [lang, setLang] = useState("en");
  useEffect(() => {
    const storedLang = localStorage.getItem("ms_biotech_lang");
    if (storedLang) {
      setLang(storedLang);
    }
  }, []);
  return <section data-section-id="3901" id="professionallogin" className="relative bg-slate-50 text-slate-900 py-32 md:py-48 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 size-96 bg-blue-600/[0.03] blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 size-96 bg-teal-500/[0.02] blur-3xl" />
      </div>

      <div ref={ref} className={`relative max-w-[880px] mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="relative bg-white border border-slate-100 backdrop-blur-sm p-10 md:p-16 lg:p-20 rounded-lg overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 w-64 h-64 opacity-[0.03] pointer-events-none">
            <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <path d="M100 20 L100 180" stroke="currentColor" strokeWidth="1.5" className="text-blue-900" />
              <circle cx="100" cy="100" r="40" stroke="currentColor" strokeWidth="1" className="text-blue-900" />
            </svg>
          </div>

          <div className="relative flex items-center gap-3 mb-8">
            <span className="size-1.5 bg-blue-600" />
            <p className="font-default uppercase tracking-[0.3em] text-[10px] font-medium text-blue-600">
              Secure Access
            </p>
          </div>

          <h2 className="font-default font-bold text-slate-900 text-5xl sm:text-6xl md:text-7xl lg:text-[72px] leading-[0.92] tracking-tight uppercase mb-8">
            Member
            <br />
            Portal Login
          </h2>

          <p className="font-default text-slate-600 text-xl md:text-2xl leading-relaxed max-w-[640px] mb-16">
            Access your professional dashboard, clinical resources, and global distribution tools.
          </p>

          <FormProvider formKey="login_form" formId="login_form" sectionName="professionallogin" className="login-form-container" id="login-form-wrapper" data-lang={lang} defaultValues={{
          email: "",
          password: "",
          interests: [],
          agree_terms: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          password: {
            required: true
          },
          agree_terms: {
            required: true
          }
        }} submitText="Sign In" successMessage="Login successful. Redirecting to your dashboard..." errorMessage="Invalid credentials. Please try again.">
            <InnerCircleForm lang={lang} />
          </FormProvider>

          <p className="font-default text-xs md:text-sm text-slate-400 mt-10 pt-8 border-t border-slate-100">
            MS Biotechnology: Your trusted partner in global medical sourcing.
          </p>
        </div>
      </div>
    </section>;
}